import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { DropdownItem, RowSelectionState } from 'design-system-web';
import { bulkAssignTaskMemberMutation } from 'graphql-queries';
import { Dropdown } from 'components';
import { AdminUser } from 'packages/lane-shared/domains/workOrder/hooks/useAssignableMembers';

const TASK_SELECTION_LIMIT = 100;

type Props = {
    rowSelection?: RowSelectionState;
    assignableUsers: AdminUser[];
    refetch?: () => void;
};

export function BulkAssigneeAction({ rowSelection, assignableUsers, refetch }: Props) {
    const { t } = useTranslation();
    const [bulkAssignTaskMember] = useMutation(bulkAssignTaskMemberMutation);

    const handleMemberAssign = async (userValue: string) => {
        if (!userValue || !rowSelection) return;

        const selectedTaskIds = Object.entries(rowSelection)
            .filter(([_, isSelected]) => isSelected)
            .map(([taskId]) => taskId);

        if (selectedTaskIds.length === 0) {
            await window.Toast.show(
                t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.noSelection')
            );
            return;
        }

        if (selectedTaskIds.length > TASK_SELECTION_LIMIT) {
            await window.Toast.show(
                t(
                    'web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.limitExceeded',
                    {
                        taskSelectionLimit: TASK_SELECTION_LIMIT,
                        selectedTaskCount: selectedTaskIds.length,
                    }
                ),
                5000
            );
            return;
        }

        const selectedUserObj = assignableUsers.find((u: AdminUser) => u.value === userValue);
        const userName = selectedUserObj?.label || userValue;

        try {
            await window.Alert.confirm({
                title: t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.confirm.title'),
                message: t(
                    'web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.confirm.message',
                    {
                        count: selectedTaskIds.length,
                        assignee: userName,
                    }
                ),
                confirmText: t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.confirm.button'),
                cancelText: t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.cancel.button'),
            });
        } catch {
            // User cancelled - no state to clean up
            return;
        }

        window.Alert.loading({
            title: t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.inProgress'),
            disableCloseOnBackgroundClick: true,
        });

        try {
            const result = await bulkAssignTaskMember({
                variables: {
                    taskIds: selectedTaskIds,
                    assignee: userValue,
                },
            });

            const { successCount, failureCount } =
                result.data?.bulkAssignTaskMember || {
                    successCount: 0,
                    failureCount: selectedTaskIds.length,
                };

            window.Alert.hide();

            if (failureCount === 0) {
                await window.Toast.show(
                    t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.success', {
                        count: successCount,
                    })
                );
            } else if (successCount === 0) {
                await window.Toast.show(
                    t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.error')
                );
            } else {
                await window.Toast.show(
                    t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.partial', {
                        successCount,
                        failureCount,
                    }),
                    5000
                );
            }

            if (refetch) {
                refetch();
            }
        } catch {
            window.Alert.hide();
            await window.Toast.show(
                t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.error')
            );
        }
    };

    return (
        <div style={{ minWidth: '200px' }}>
            <Dropdown
                isSearchable
                value=""
                items={assignableUsers || []}
                onChange={(item: DropdownItem<string>) => {
                    if (item.value) {
                        handleMemberAssign(item.value);
                    }
                }}
                placeholder={t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.selectMember')}
                doTranslation={false}
                testId="bulk-task-assignee-select"
            />
        </div>
    );
}
